import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { DAISY_THEMES, DEFAULT_THEME } from './daisyThemes';

type AppThemeRow = {
  theme_name: string;
  is_enabled: boolean;
  is_default: boolean;
};

type ThemeContextValue = {
  themes: AppThemeRow[];
  enabledThemes: string[];
  defaultTheme: string;
  activeTheme: string;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  setActiveTheme: (themeName: string) => Promise<void>;
  refreshThemes: () => Promise<void>;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const getCachedTheme = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('theme');
  } catch {
    return null;
  }
};

const cacheTheme = (themeName: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('theme', themeName);
  } catch {
    // Ignore storage failures.
  }
};

const applyTheme = (themeName: string) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', themeName);
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themes, setThemes] = useState<AppThemeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTheme, setActiveThemeState] = useState<string>(() => getCachedTheme() || DEFAULT_THEME);
  const [userTheme, setUserTheme] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const enabledThemes = useMemo(() => {
    const enabled = themes
      .filter((theme) => theme.is_enabled && DAISY_THEMES.includes(theme.theme_name))
      .map((theme) => theme.theme_name);
    return enabled.length ? enabled : [DEFAULT_THEME];
  }, [themes]);

  const defaultTheme = useMemo(() => {
    const defaultRow = themes.find(
      (theme) => theme.is_default && DAISY_THEMES.includes(theme.theme_name),
    );
    return defaultRow?.theme_name ?? DEFAULT_THEME;
  }, [themes]);

  const refreshThemes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('app_themes')
        .select('theme_name, is_enabled, is_default');
      if (fetchError) throw fetchError;
      setThemes(data ?? []);
    } catch (err: any) {
      setError(err?.message ?? 'Unable to load themes.');
      setThemes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshThemes();
  }, [refreshThemes]);

  useEffect(() => {
    applyTheme(activeTheme);
    cacheTheme(activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    let isMounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setUserId(data.session?.user?.id ?? null);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setUserId(newSession?.user?.id ?? null);
    });
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setUserTheme(null);
      return;
    }
    let isMounted = true;
    (async () => {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('prefered_theme')
        .eq('id', userId)
        .maybeSingle();
      if (!isMounted) return;
      if (fetchError) {
        setUserTheme(null);
        return;
      }
      setUserTheme(data?.prefered_theme ?? null);
    })();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    const cachedTheme = getCachedTheme();
    const nextTheme =
      (userTheme && enabledThemes.includes(userTheme) && userTheme) ||
      (cachedTheme && enabledThemes.includes(cachedTheme) && cachedTheme) ||
      (enabledThemes.includes(defaultTheme) && defaultTheme) ||
      enabledThemes[0] ||
      DEFAULT_THEME;
    setActiveThemeState((prev) => (prev === nextTheme ? prev : nextTheme));
  }, [defaultTheme, enabledThemes, userTheme]);

  const setActiveTheme = useCallback(
    async (themeName: string) => {
      if (!DAISY_THEMES.includes(themeName)) return;
      if (!enabledThemes.includes(themeName)) return;
      const previous = activeTheme;
      setActiveThemeState(themeName);
      cacheTheme(themeName);
      setSaveError(null);
      if (!userId) return;
      setSaving(true);
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ prefered_theme: themeName })
        .eq('id', userId);
      setSaving(false);
      if (updateError) {
        setSaveError(updateError.message ?? 'Unable to save theme preference.');
        setActiveThemeState(previous);
        cacheTheme(previous);
      }
    },
    [activeTheme, enabledThemes, userId],
  );

  const value = useMemo(
    () => ({
      themes,
      enabledThemes,
      defaultTheme,
      activeTheme,
      loading,
      error,
      saving,
      saveError,
      setActiveTheme,
      refreshThemes,
    }),
    [
      activeTheme,
      defaultTheme,
      enabledThemes,
      error,
      loading,
      refreshThemes,
      saveError,
      saving,
      setActiveTheme,
      themes,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
